import React, { useState, useEffect, useCallback } from 'react';
import { learnerApi, WrongAnswerRow, WrongHeatmapRow } from '../../api/learner';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Loader2, Flame } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination } from '../../components/shared/Pagination';

interface PageProps {
  isDark: boolean;
}

const PAGE_SIZE = 25;

export default function WrongAnswersPage({ isDark }: PageProps) {
  const [tab, setTab] = useState<'list' | 'heatmap'>('list');

  // List state
  const [items, setItems] = useState<WrongAnswerRow[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [userIdFilter, setUserIdFilter] = useState('');
  const [questionIdFilter, setQuestionIdFilter] = useState('');
  const [minWrongCount, setMinWrongCount] = useState('');

  // Heatmap state
  const [heatmap, setHeatmap] = useState<WrongHeatmapRow[]>([]);
  const [heatmapLoading, setHeatmapLoading] = useState(false);

  const fetchList = useCallback(async () => {
    setListLoading(true);
    try {
      const params: any = { page, pageSize: PAGE_SIZE };
      if (userIdFilter.trim()) params.userId = userIdFilter.trim();
      if (questionIdFilter.trim()) params.questionId = Number(questionIdFilter.trim());
      if (minWrongCount.trim()) params.minWrongCount = Number(minWrongCount.trim());
      const res = await learnerApi.listWrongAnswers(params);
      setItems(res.items);
      setTotal(res.total);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Не удалось загрузить ошибки');
    } finally {
      setListLoading(false);
    }
  }, [page, userIdFilter, questionIdFilter, minWrongCount]);

  const fetchHeatmap = useCallback(async () => {
    setHeatmapLoading(true);
    try {
      const res = await learnerApi.wrongHeatmap(100);
      setHeatmap(res.items);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Не удалось загрузить тепловую карту');
    } finally {
      setHeatmapLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'list') fetchList();
    else fetchHeatmap();
  }, [tab, fetchList, fetchHeatmap]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const formatDate = (s: string | null) => {
    if (!s) return '—';
    return new Date(s).toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const maxHeatmapWrong = heatmap.length > 0 ? Math.max(...heatmap.map((r) => r.total_wrong)) : 1;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-[#FFE3E3] flex items-center justify-center">
          <Flame className="w-6 h-6 text-[#F15656]" />
        </div>
        <div>
          <h1 className={cn(
            'text-2xl lg:text-3xl font-bold tracking-tight',
            isDark ? 'text-white' : 'text-[#111111]'
          )}>
            Лог ошибок
          </h1>
          <p className={cn('text-sm mt-0.5', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Где ученики ошибаются — лог по пользователям + тепловая карта самых сложных вопросов
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as 'list' | 'heatmap')}>
        <TabsList className={cn(
          'rounded-xl p-1',
          isDark ? 'bg-[#1A1A22]' : 'bg-[#EFEAE0]'
        )}>
          <TabsTrigger value="list" className="rounded-lg">По юзерам</TabsTrigger>
          <TabsTrigger value="heatmap" className="rounded-lg">Тепловая карта вопросов</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === 'list' && (
        <>
          <Card className={cn(
            'p-4 rounded-2xl border-0',
            isDark ? 'bg-[#1A1A22] shadow-md shadow-black/30' : 'bg-white shadow-md shadow-black/[0.04]'
          )}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Input
                placeholder="ID юзера (UUID)"
                value={userIdFilter}
                onChange={(e) => { setUserIdFilter(e.target.value); setPage(1); }}
                className="rounded-xl"
              />
              <Input
                type="number"
                placeholder="ID вопроса"
                value={questionIdFilter}
                onChange={(e) => { setQuestionIdFilter(e.target.value); setPage(1); }}
                className="rounded-xl"
              />
              <Input
                type="number"
                placeholder="Мин. число ошибок"
                value={minWrongCount}
                onChange={(e) => { setMinWrongCount(e.target.value); setPage(1); }}
                className="rounded-xl"
              />
            </div>
          </Card>

          <Card className={cn(
            'rounded-2xl border-0 overflow-hidden',
            isDark ? 'bg-[#1A1A22] shadow-md shadow-black/30' : 'bg-white shadow-md shadow-black/[0.04]'
          )}>
            {listLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 animate-spin text-[#C9A227]" />
              </div>
            ) : items.length === 0 ? (
              <div className={cn('text-center py-16 text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
                Нет записей об ошибках по заданным фильтрам.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Пользователь</TableHead>
                    <TableHead>Вопрос</TableHead>
                    <TableHead>Кол-во ошибок</TableHead>
                    <TableHead>Последняя ошибка</TableHead>
                    <TableHead>Контекст</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => (
                    <TableRow key={it.id}>
                      <TableCell className="font-mono text-xs">{it.user_id.slice(0, 8)}…</TableCell>
                      <TableCell className="font-mono">Q#{it.question_id}</TableCell>
                      <TableCell className="font-bold text-[#F15656]">{it.wrong_count}</TableCell>
                      <TableCell className="text-sm">{formatDate(it.last_wrong_at)}</TableCell>
                      <TableCell className="text-sm capitalize">{it.last_session_kind ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                total={total}
                itemsPerPage={PAGE_SIZE}
                onPageChange={setPage}
                isDark={isDark}
              />
            )}
          </Card>
        </>
      )}

      {tab === 'heatmap' && (
        <Card className={cn(
          'rounded-2xl border-0 p-5',
          isDark ? 'bg-[#1A1A22] shadow-md shadow-black/30' : 'bg-white shadow-md shadow-black/[0.04]'
        )}>
          <h3 className={cn('text-base font-semibold mb-4', isDark ? 'text-white' : 'text-[#111111]')}>
            Top 100 hardest questions across all learners
          </h3>
          {heatmapLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-[#C9A227]" />
            </div>
          ) : heatmap.length === 0 ? (
            <div className={cn('text-center py-12 text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
              Пока нет данных.
            </div>
          ) : (
            <div className="space-y-2">
              {heatmap.map((row) => {
                const ratio = row.total_wrong / maxHeatmapWrong;
                return (
                  <div
                    key={row.question_id}
                    className={cn(
                      'relative rounded-xl px-4 py-2.5 overflow-hidden',
                      isDark ? 'bg-[#0F0F17]' : 'bg-[#F5F3EF]'
                    )}
                  >
                    <div
                      className="absolute inset-y-0 left-0 bg-[#F15656]/15"
                      style={{ width: `${Math.max(ratio * 100, 3)}%` }}
                    />
                    <div className="relative flex items-center justify-between gap-4">
                      <span className={cn('font-mono font-semibold', isDark ? 'text-white' : 'text-[#111111]')}>
                        Q#{row.question_id}
                      </span>
                      <div className="flex items-center gap-6 text-sm">
                        <div className="flex items-center gap-1.5">
                          <span className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>Всего ошибок</span>
                          <span className="font-bold text-[#F15656]">{row.total_wrong}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>Пользователи</span>
                          <span className={cn('font-bold', isDark ? 'text-white' : 'text-[#111111]')}>
                            {row.users_affected}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
